/**
 * Style reminder (The Press Desk): party color is the single source of visual meaning.
 * Never recolor parties arbitrarily. Conventional Malaysian coalition colors used below.
 *
 * Sources (Wikipedia infoboxes, official party branding):
 *   BN  — Royal blue + sky white
 *   PH  — Red + white
 *   PN  — Dark blue + white
 *   BERSAMA — Yellow + dark blue
 *   MUDA — Black
 *   PSM  — Red (socialist red)
 */

export interface PartyMeta {
  code: string;
  name: string;
  full: string;
  color: string;
  text: string; // foreground for color chips
}

export const PARTIES: Record<string, PartyMeta> = {
  // ── Coalitions ──
  BN:       { code: "BN",       name: "BN",       full: "Barisan Nasional",                  color: "#003087", text: "#ffffff" }, // Royal blue
  PH:       { code: "PH",       name: "PH",       full: "Pakatan Harapan",                   color: "#D7261E", text: "#ffffff" }, // Red
  PN:       { code: "PN",       name: "PN",       full: "Perikatan Nasional",                color: "#002B5C", text: "#ffffff" }, // Dark blue (NOT teal)
  // ── Standalone parties ──
  MUDA:     { code: "MUDA",     name: "MUDA",     full: "Malaysian United Democratic Alliance", color: "#1A1A1A", text: "#ffffff" }, // Black
  BERSAMA:  { code: "BERSAMA",  name: "BERSAMA",  full: "Parti Bersama Malaysia",            color: "#E8B800", text: "#1a2744" }, // Yellow with dark blue text
  PSM:      { code: "PSM",      name: "PSM",      full: "Parti Sosialis Malaysia",           color: "#B11226", text: "#ffffff" }, // Socialist red
  POAM:     { code: "POAM",     name: "POAM",     full: "Parti Orang Asli Malaysia",         color: "#4A7C59", text: "#ffffff" }, // Forest green
  WARISAN:  { code: "WARISAN",  name: "WARISAN",  full: "Parti Warisan",                     color: "#00A19A", text: "#ffffff" }, // Teal
  PEJUANG:  { code: "PEJUANG",  name: "PEJUANG",  full: "Parti Pejuang Tanah Air",           color: "#7A4DA6", text: "#ffffff" }, // Purple
  MIPP:     { code: "MIPP",     name: "MIPP",     full: "Malaysian Indian People's Party",   color: "#E08A1E", text: "#ffffff" }, // Orange
  PBM:      { code: "PBM",      name: "PBM",      full: "Parti Bangsa Malaysia",             color: "#C77D1A", text: "#ffffff" },
  PUTRA:    { code: "PUTRA",    name: "PUTRA",    full: "Parti Bumiputera Perkasa Malaysia", color: "#6B7280", text: "#ffffff" },
  ASLI:     { code: "ASLI",     name: "ASLI",     full: "Parti Bumiputera Asli Malaysia",    color: "#92561C", text: "#ffffff" },
  IND:      { code: "IND",      name: "IND",      full: "Independent",                       color: "#8A8A8A", text: "#ffffff" },
  OTHER:    { code: "OTHER",    name: "Other",    full: "Other",                             color: "#A1A1AA", text: "#ffffff" },
};

/**
 * Sub-party (component party) metadata.
 * Each sub-party has its own colour for inline badges.
 * Colours sourced from Wikipedia infoboxes / official branding.
 */
export interface SubPartyMeta {
  code: string;
  name: string;
  full: string;
  color: string;
  text: string;
  coalition?: string; // parent coalition code
}

export const SUBPARTIES: Record<string, SubPartyMeta> = {
  // ── BN component parties ──
  UMNO:     { code: "UMNO",     name: "UMNO",     full: "United Malays National Organisation",  color: "#003087", text: "#ffffff", coalition: "BN" },
  MCA:      { code: "MCA",      name: "MCA",      full: "Malaysian Chinese Association",        color: "#003087", text: "#ffffff", coalition: "BN" },
  MIC:      { code: "MIC",      name: "MIC",      full: "Malaysian Indian Congress",            color: "#006400", text: "#ffffff", coalition: "BN" },
  // ── PH component parties ──
  DAP:      { code: "DAP",      name: "DAP",      full: "Democratic Action Party",              color: "#D7261E", text: "#ffffff", coalition: "PH" },
  PKR:      { code: "PKR",      name: "PKR",      full: "People's Justice Party",               color: "#00A3E0", text: "#ffffff", coalition: "PH" },
  AMANAH:   { code: "AMANAH",   name: "AMANAH",   full: "Parti Amanah Negara",                  color: "#F57F20", text: "#ffffff", coalition: "PH" },
  // ── PN component parties ──
  PAS:      { code: "PAS",      name: "PAS",      full: "Malaysian Islamic Party",              color: "#009B4D", text: "#ffffff", coalition: "PN" },
  BERSATU:  { code: "BERSATU",  name: "BERSATU",  full: "Malaysian United Indigenous Party",    color: "#D31027", text: "#ffffff", coalition: "PN" },
  GERAKAN:  { code: "GERAKAN",  name: "GERAKAN",  full: "Malaysian People's Movement Party",   color: "#D32F2F", text: "#ffffff", coalition: "PN" },
  WAWASAN:  { code: "WAWASAN",  name: "WAWASAN",  full: "National Vision Party",                color: "#002B5C", text: "#ffffff", coalition: "PN" },
  // ── PN-allied (contested under PN banner) ──
  "PEJUANG-PN": { code: "PEJUANG", name: "PEJUANG", full: "Parti Pejuang Tanah Air", color: "#7A4DA6", text: "#ffffff", coalition: "PN" },
  "MIPP-PN":    { code: "MIPP",    name: "MIPP",    full: "Malaysian Indian People's Party", color: "#E08A1E", text: "#ffffff", coalition: "PN" },
  // ── Standalone (sub = party itself) ──
  MUDA:     { code: "MUDA",     name: "MUDA",     full: "Malaysian United Democratic Alliance", color: "#1A1A1A", text: "#ffffff" },
  BERSAMA:  { code: "BERSAMA",  name: "BERSAMA",  full: "Parti Bersama Malaysia",              color: "#E8B800", text: "#1a2744" },
  PSM:      { code: "PSM",      name: "PSM",      full: "Parti Sosialis Malaysia",             color: "#B11226", text: "#ffffff" },
  POAM:     { code: "POAM",     name: "POAM",     full: "Parti Orang Asli Malaysia",           color: "#4A7C59", text: "#ffffff" },
  IND:      { code: "IND",      name: "IND",      full: "Independent",                         color: "#8A8A8A", text: "#ffffff" },
};

export function party(code: string): PartyMeta {
  if (code === "OTHER_DIM")
    return { code: "OTHER_DIM", name: "—", full: "Other", color: "#d6d3cd", text: "#555" };
  return PARTIES[code] ?? PARTIES.OTHER;
}

export function partyColor(code: string): string {
  return party(code).color;
}

/** Get the sub-party metadata. Falls back to the coalition/party colour if unknown. */
export function subParty(code: string): SubPartyMeta {
  return SUBPARTIES[code] ?? { code, name: code, full: code, color: party(code).color, text: "#ffffff" };
}

/**
 * Format a display label like "BN · UMNO" or just "MUDA" for standalone parties.
 * If sub is the same as the party code, return just the party name.
 */
export function partyLabel(partyCode: string, sub?: string): string {
  if (!sub || sub === partyCode) return party(partyCode).name;
  return `${party(partyCode).name} · ${sub}`;
}
